import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { postFollowUp } from '@/lib/api/v2/follow-ups'

const NOTE_MAX_LENGTH = 200

interface FollowUpDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  clientName: string
  issuedAtLabel?: string | null
  /**
   * Called after a successful POST so the parent can refresh the dashboard
   * (urgency bucket + counts + lastFollowUpAt must re-fetch).
   */
  onSuccess?: () => void
}

export function FollowUpDialog({
  open,
  onOpenChange,
  projectId,
  clientName,
  issuedAtLabel,
  onSuccess,
}: FollowUpDialogProps) {
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Reset state on close so the next open starts clean.
  useEffect(() => {
    if (!open) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setNote('')
      setSubmitting(false)
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [open])

  // Focus textarea on open (next tick after Dialog mount).
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => textareaRef.current?.focus(), 50)
      return () => clearTimeout(t)
    }
  }, [open])

  const trimmed = note.trim()
  const canSubmit = trimmed.length > 0 && trimmed.length <= NOTE_MAX_LENGTH && !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      await postFollowUp(projectId, { note: trimmed })
      toast.success('Relance enregistrée')
      onSuccess?.()
      onOpenChange(false)
    } catch (err) {
      console.error('postFollowUp failed', err)
      toast.error("Impossible d'enregistrer la relance")
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Logger une relance</DialogTitle>
        </DialogHeader>

        <div className="py-2 space-y-3">
          <div className="text-sm text-muted-foreground">
            <div className="font-medium text-foreground">{clientName}</div>
            {issuedAtLabel && <div>Devis envoyé le {issuedAtLabel}</div>}
          </div>

          <div className="space-y-1">
            <Textarea
              ref={textareaRef}
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
              placeholder="Rappelé, attend retour conjoint…"
              rows={3}
              maxLength={NOTE_MAX_LENGTH}
              data-testid="follow-up-note-input"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault()
                  void handleSubmit()
                }
              }}
            />
            <div className="text-xs text-muted-foreground text-right">
              {trimmed.length} / {NOTE_MAX_LENGTH}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="follow-up-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="follow-up-submit-btn"
          >
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
